import type { ChamberSettingsFormState } from "./settingsData";

// Design (11) prorated fees (CMP Dashboard 7020-7073, 7276-7328, maths
// 14657-14737; Thunderbay Chamber Set Up for Multi-Billing Dates).
// Twelve Membership Period cells start at the fiscal year start month. A
// cell's default period runs up to the next renewal billing month, and its
// default amount is the 12 month fee x months / 12, to the cent.

const fullMonthLabels = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const prorationMonthShortLabels = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

type ProrationSetup = Pick<
  ChamberSettingsFormState["setup"],
  | "fiscalYearStartMonth"
  | "membershipRenewalFixedDateEnabled"
  | "membershipRenewalFixedDateMonth"
  | "multipleBillingDates"
>;

const getMonthIndex = (label?: string | null) =>
  fullMonthLabels.indexOf((label || "").trim());

// The renewal billing months: the Fix Date, or both Multi-Billing Dates when
// both are selected. Empty when prorated fees cannot apply.
export const getProrationBillingMonthIndexes = (
  setup: ProrationSetup
): number[] => {
  const multipleBillingDates = setup.multipleBillingDates;
  if (multipleBillingDates?.enabled) {
    const months = [
      getMonthIndex(multipleBillingDates.firstBillingDate.month),
      getMonthIndex(multipleBillingDates.secondBillingDate.month),
    ];
    return months.every((month) => month >= 0) &&
      multipleBillingDates.firstBillingDate.day.trim() &&
      multipleBillingDates.secondBillingDate.day.trim()
      ? months
      : [];
  }
  if (setup.membershipRenewalFixedDateEnabled) {
    const month = getMonthIndex(setup.membershipRenewalFixedDateMonth);
    return month >= 0 ? [month] : [];
  }
  return [];
};

export const isProratedPricingActive = (
  setup: ProrationSetup,
  membership: Pick<
    ChamberSettingsFormState["membership"],
    "globalToggleState" | "enabledPaymentSchedules"
  >
) =>
  membership.globalToggleState.prorate &&
  membership.enabledPaymentSchedules.annual &&
  getProrationBillingMonthIndexes(setup).length > 0;

// Months from a start month to the nearest billing month (12 when the start
// month is a billing month). Without billing months: the rest of the fiscal
// year, as the design falls back.
export const getDefaultProrationMonths = ({
  billingMonthIndexes,
  cellIndex,
  startMonthIndex,
}: {
  billingMonthIndexes: number[];
  cellIndex: number;
  startMonthIndex: number;
}) =>
  billingMonthIndexes.length === 0
    ? 12 - cellIndex
    : Math.min(
        ...billingMonthIndexes.map(
          (billingMonth) => (billingMonth - startMonthIndex + 12) % 12 || 12
        )
      );

export const getProratedAmountInDollars = (fee: number, months: number) =>
  Math.round(((fee * months) / 12) * 100) / 100;

const formatMonthYear = (absoluteMonth: number) =>
  `${prorationMonthShortLabels[((absoluteMonth % 12) + 12) % 12]} ${Math.floor(
    absoluteMonth / 12
  )}`;

export type ProrationCell = {
  cellIndex: number;
  monthLabel: string; // "Nov" (the stored month key)
  startAbsoluteMonth: number;
  startLabel: string; // "Nov 2026"
  defaultMonths: number;
};

// The 12 cells of the current fiscal year, in fiscal order.
export const getProrationCells = (
  setup: ProrationSetup,
  now: Date = new Date()
): ProrationCell[] => {
  const fiscalStartMonthIndex = Math.max(
    0,
    getMonthIndex(setup.fiscalYearStartMonth)
  );
  const fiscalStartYear =
    now.getUTCMonth() >= fiscalStartMonthIndex
      ? now.getUTCFullYear()
      : now.getUTCFullYear() - 1;
  const billingMonthIndexes = getProrationBillingMonthIndexes(setup);

  return prorationMonthShortLabels.map((_, cellIndex) => {
    const startAbsoluteMonth =
      fiscalStartYear * 12 + fiscalStartMonthIndex + cellIndex;
    const startMonthIndex = startAbsoluteMonth % 12;
    return {
      cellIndex,
      monthLabel: prorationMonthShortLabels[startMonthIndex],
      startAbsoluteMonth,
      startLabel: formatMonthYear(startAbsoluteMonth),
      defaultMonths: getDefaultProrationMonths({
        billingMonthIndexes,
        cellIndex,
        startMonthIndex,
      }),
    };
  });
};

// Design: the end label is inclusive ("Jan 2026" + 12 months ends "Dec 2026").
export const getProrationEndLabel = (cell: ProrationCell, months: number) =>
  formatMonthYear(cell.startAbsoluteMonth + months - 1);

// "Mar 2027" -> months from the cell start (inclusive), or null.
export const getProrationMonthsForEndLabel = (
  cell: ProrationCell,
  endLabel: string
): number | null => {
  const match = endLabel.trim().match(/^([A-Za-z]{3})[A-Za-z]*\.?\s+(\d{4})$/);
  if (!match) return null;
  const monthIndex = prorationMonthShortLabels.findIndex(
    (label) => label.toLowerCase() === match[1].toLowerCase()
  );
  if (monthIndex < 0) return null;
  const months =
    Number(match[2]) * 12 + monthIndex - cell.startAbsoluteMonth + 1;
  return months > 0 ? months : null;
};

export const formatProrationMonths = (months: number) =>
  `${months} ${months === 1 ? "month" : "months"}`;
